import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { HiOutlineMinus, HiOutlinePlus } from "react-icons/hi";

import {
  DcfModal,
  EbitdaModal,
  EbitModal,
  SimpleFiscaleModal,
  ProductivityModal,
  NetIncomeAverageModal,
  CashFlowAndCAFModal,
  MbaAverageModal,
  PatrimonialeModal,
} from "../components/SMEModals";
import KnownBPTable from "../components/KnownBpTable";
import EbitdaTable from "../components/EbitdaTable";
import NwcTable from "../components/NwcTable";
import NetTotal from "../components/NetTotal";
import api from "../../../services/api";
import toast from "react-hot-toast";

const Valuation = ({ company, setCompany }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalOpened, setModalOpened] = useState("");

  useEffect(() => {
    if (searchParams.has("modal")) setModalOpened(searchParams.get("modal"));
  }, [searchParams]);

  const handleCloseModal = () => {
    setSearchParams({});
    setModalOpened(null);
  };

  const handleSubmit = async (values) => {
    try {
      const res = await api.put(`/company/${company._id}`, values);
      if (!res.ok) throw res;
      toast.success("Les informations ont été mises à jour");
      setCompany(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue lors de la mise à jour des informations");
    }
  };

  if (!company) return <></>;

  const weighting_total =
    Math.round(company.weighting_dcf * 100) +
    Math.round(company.weighting_ebitda * 100) +
    Math.round(company.weighting_ebit * 100) +
    Math.round(company.weighting_fiscale * 100) +
    Math.round(company.weighting_productivity * 100) +
    Math.round(company.weighting_net_income_average * 100) +
    Math.round(company.weighting_cashflow * 100) +
    Math.round(company.weighting_mba_average * 100) +
    Math.round(company.weighting_patrimonial * 100);

  return (
    <div>
      {modalOpened
        ? {
            dcf: <DcfModal company={company} onSubmit={handleSubmit} onClose={handleCloseModal} />,
            ebitda: <EbitdaModal company={company} onSubmit={handleSubmit} onClose={handleCloseModal} />,
            ebit: <EbitModal company={company} onSubmit={handleSubmit} onClose={handleCloseModal} />,
            fiscale: <SimpleFiscaleModal company={company} onSubmit={handleSubmit} onClose={handleCloseModal} />,
            productivity: <ProductivityModal company={company} onSubmit={handleSubmit} onClose={handleCloseModal} />,
            net: <NetIncomeAverageModal company={company} onSubmit={handleSubmit} onClose={handleCloseModal} />,
            caf: <CashFlowAndCAFModal company={company} onSubmit={handleSubmit} onClose={handleCloseModal} />,
            mba: <MbaAverageModal company={company} onSubmit={handleSubmit} onClose={handleCloseModal} />,
            patrimoniale: <PatrimonialeModal company={company} onSubmit={handleSubmit} onClose={handleCloseModal} />,
          }[modalOpened]
        : null}

      <div className="border border-secondary p-3 rounded-lg flex flex-col items-center">
        <h3 className="font-semibold text-lg">Valorisation finale</h3>
        <p className="text-lg font-bold">{Math.round(company.valuation_global || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</p>
      </div>
      <div className="border border-secondary p-3 rounded-lg">
        <h3 className="font-semibold text-lg">Première année de prévision</h3>
        <select value={company.year_4} onChange={(e) => handleSubmit({ year_4: Number(e.target.value) })} className="select w-full">
          {[2025, 2026, 2027, 2028, 2029, 2030].map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <FinancialInfos company={company} onSubmit={handleSubmit} />
      </div>

      <div className="grid grid-cols-4 gap-4 mt-4">
        <Card
          title="Méthode DCF"
          weighting={company.weighting_dcf}
          value={company.valuation_dcf}
          onChange={(value) => handleSubmit({ valuation: true, weighting_dcf: value })}
          link="?modal=dcf"
        />
        <Card
          title="Méthode EBE"
          weighting={company.weighting_ebitda}
          value={company.valuation_ebitda}
          onChange={(value) => handleSubmit({ valuation: true, weighting_ebitda: value })}
          link="?modal=ebitda"
        />
        <Card
          title="Méthode EBIT"
          weighting={company.weighting_ebit}
          value={company.valuation_ebit}
          onChange={(value) => handleSubmit({ valuation: true, weighting_ebit: value })}
          link="?modal=ebit"
        />

        <Card
          title="Méthode fiscale"
          weighting={company.weighting_fiscale}
          value={company.valuation_fiscale}
          onChange={(value) => handleSubmit({ valuation: true, weighting_fiscale: value })}
          link="?modal=fiscale"
        />

        <Card
          title="Méthode valeur de productivité"
          weighting={company.weighting_productivity}
          value={company.valuation_productivity}
          onChange={(value) => handleSubmit({ valuation: true, weighting_productivity: value })}
          link="?modal=productivity"
        />

        <Card
          title="Moyenne des revenus nets"
          weighting={company.weighting_net_income_average}
          value={company.valuation_net_income_average}
          onChange={(value) => handleSubmit({ valuation: true, weighting_net_income_average: value })}
          link="?modal=net"
        />

        <Card
          title="Flux de trésorerie et CAF"
          weighting={company.weighting_cashflow}
          value={company.valuation_cashflow}
          onChange={(value) => handleSubmit({ valuation: true, weighting_cashflow: value })}
          link="?modal=caf"
        />

        <Card
          title="Moyenne des MBA"
          weighting={company.weighting_mba_average}
          value={company.valuation_mba_average}
          onChange={(value) => handleSubmit({ valuation: true, weighting_mba_average: value })}
          link="?modal=mba"
        />

        <Card
          title="Méthode patrimoniale"
          weighting={company.weighting_patrimonial}
          value={company.valuation_patrimonial}
          onChange={(value) => handleSubmit({ valuation: true, weighting_patrimonial: value })}
          link="?modal=patrimoniale"
        />
        <div className="border border-gray-200 rounded-xl p-4 flex flex-col items-center">
          <h3 className="text-lg font-semibold mb-4 text-center">Pondération totale</h3>
          <div className="space-y-2 w-full px-4">
            {weighting_total > 100 ? (
              <div className="flex flex-col items-center justify-between gap-3">
                <p className="text-lg font-bold text-red-500">{`${weighting_total}%`}</p>
                <p className="text-lg text-red-500">La pondération totale doit être de 100%.</p>
              </div>
            ) : weighting_total < 100 ? (
              <div className="flex flex-col items-center justify-between gap-3">
                <p className="text-lg font-bold text-blue-500">{`${weighting_total}%`}</p>
                <p className="text-lg text-blue-500">La pondération totale doit être de 100%.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-between gap-3">
                <p className="text-lg font-bold">{`${weighting_total}%`}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FinancialInfos = ({ company, onSubmit }) => {
  const [values, setValues] = useState({ ...company });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setValues(company);
  }, [company]);

  return (
    <div className={`border border-secondary p-3 transition-all rounded-lg ${isOpen ? "border-black" : "hover:border-black"}`}>
      <button onClick={() => setIsOpen(!isOpen)} className="flex justify-between items-center w-full">
        <h3 className="font-semibold text-lg">Informations financières</h3>
        {isOpen ? <HiOutlineMinus className="w-5" /> : <HiOutlinePlus className="w-5" />}
      </button>

      <div className={`space-y-2 overflow-hidden transition-all duration-300 ease-in-out text-slate-600 text-sm ${isOpen ? "opacity-100 mt-2 " : "h-0 opacity-0"}`}>
        <div className="flex items-center justify-end">
          <button onClick={() => onSubmit(values)} className="primary-button">
            Enregistrer
          </button>
        </div>
        <div className="space-y-4">
          <KnownBPTable values={values} onChange={setValues} />
          <EbitdaTable values={values} />
          <NwcTable values={values} />
          <NetTotal values={values} />
        </div>
      </div>
    </div>
  );
};

const Card = ({ title, weighting, value, onChange, link }) => (
  <div className="border border-gray-200 rounded-xl p-4 flex flex-col items-center">
    <h3 className="text-lg font-semibold mb-4 text-center">{title}</h3>
    <div className="space-y-2 w-full px-4">
      <div className="flex items-center justify-between">
        <label className="text-lg">Pondération</label>
        <div className="flex items-center">
          <input type="number" min="0" max="100" className="input w-fit p-2" value={weighting * 100} onChange={(e) => onChange(Number(e.target.value) / 100)} />
          <span className="pl-1">%</span>
        </div>
        {/* <select value={weighting} onChange={(e) => onChange(Number(e.target.value))} className="select w-20 p-2">
          {[0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1].map((v) => (
            <option key={v} value={v}>
              {v.toLocaleString("fr-FR", { style: "percent" })}
            </option>
          ))}
        </select> */}
      </div>
      <div className="flex items-center justify-between space-x-1">
        <label className="text-lg">Valorisation</label>
        <p className="text-lg font-bold">{Math.round(value || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</p>
      </div>
    </div>
    <Link to={link} className="primary-button mt-4">
      Voir le details
    </Link>
  </div>
);

export default Valuation;
